import { useMemo, useState } from "react";
import { Button, DataTable, SectionCard, type ColumnDef } from "@/deps/ui-library";
import { useAccounts, useRenameAccount } from "@/api/queries";
import type { Account } from "@/api/types";
import { useCan } from "@/app/session";
import { ChartRolesCard } from "./ChartRolesCard";
import { RenameDialog } from "./RenameDialog";

/** The accounts documents can post to. Adding one comes with Banking (plan 4.5). */
export function ChartTab() {
  const { data, isPending } = useAccounts();
  const rows = useMemo(() => data ?? [], [data]);
  const can = useCan();
  const editable = can("accounting.coa.manage");
  const rename = useRenameAccount();
  const [editing, setEditing] = useState<Account | null>(null);

  const columns = useMemo<Array<ColumnDef<Account>>>(
    () => [
      {
        id: "code",
        header: "Code",
        cell: ({ row: { original } }) => (
          <span className="num text-foreground">{original.code}</span>
        ),
      },
      {
        id: "name",
        header: "Account",
        cell: ({ row: { original } }) => <span className="text-foreground">{original.name}</span>,
      },
      {
        id: "group",
        header: "Type",
        cell: ({ row: { original } }) => <span className="text-foreground">{original.group}</span>,
      },
      ...(editable
        ? [
            {
              id: "rename",
              header: "",
              meta: { textAlign: "right" as const },
              cell: ({ row: { original } }: { row: { original: Account } }) => (
                <Button
                  colorScheme="default"
                  variant="ghost"
                  size="xs"
                  aria-label={`Rename ${original.name}`}
                  onClick={() => setEditing(original)}
                >
                  Rename
                </Button>
              ),
            },
          ]
        : []),
    ],
    [editable],
  );

  return (
    <div className="flex flex-col gap-4">
      {editable ? <ChartRolesCard /> : null}
      <SectionCard title="Chart of accounts" flush>
        {isPending ? (
          <p className="text-muted-foreground p-4 text-sm">Loading</p>
        ) : (
          <DataTable
            aria-label="Chart of accounts"
            size="sm"
            enableSorting={false}
            columns={columns}
            data={rows}
            getRowId={(row) => row.id}
            pagination={{ mode: "manual", show: false }}
          />
        )}
        {editing ? (
          <RenameDialog
            title={`Rename account ${editing.code}`}
            note="Its code and everything posted to it stay the same."
            fields={[{ key: editing.id, label: "Account name", value: editing.name }]}
            onSave={(changed) =>
              Promise.all(changed.map(({ key, name }) => rename.mutateAsync({ id: key, name })))
            }
            onClose={() => setEditing(null)}
          />
        ) : null}
      </SectionCard>
    </div>
  );
}
